import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

type ValidationOptions = {
    isRequired?: boolean;
    maxLength?: number;
    // Custom validation function
    customValidate?: (value: string) => boolean;
};

type UseDebouncedSaveFieldOptions = {
    savedValue: string;
    handleSave: (newValue: string) => Promise<any>;
    validation?: ValidationOptions;
};

type UseDebouncedSaveFieldReturn = {
    value: string;
    errorMessage: string | null | undefined;
    onChange: (newValue: string) => void;
    onBlur: () => void;
};

/**
 * Custom hook to manage form field state with debounced saving and validation.
 * Handles the common pattern of:
 * - Syncing local state with saved value
 * - Validating on change and triggering save
 * - Resetting to saved value on blur if invalid
 */
export function useDebouncedSaveField(
    options: UseDebouncedSaveFieldOptions
): UseDebouncedSaveFieldReturn {
    const {savedValue, handleSave, validation = {}} = options;
    const {isRequired = false, maxLength, customValidate} = validation;

    const [value, setValue] = React.useState<string>(savedValue);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    React.useEffect(() => {
        setValue(savedValue);
    }, [savedValue]);

    const isValid = React.useCallback(
        (valueToValidate: string): boolean => {
            if (isRequired && !valueToValidate.trim()) {
                return false;
            }

            if (maxLength != null && valueToValidate.length > maxLength) {
                return false;
            }

            // Check custom validation
            if (customValidate && !customValidate(valueToValidate)) {
                return false;
            }

            return true;
        },
        [isRequired, maxLength, customValidate]
    );

    // Track the last saved value to avoid saving unchanged values
    const lastSavedValueRef = React.useRef<string>(savedValue);

    // Update the ref when savedValue changes
    React.useEffect(() => {
        lastSavedValueRef.current = savedValue;
    }, [savedValue]);

    const onChange = React.useCallback(
        (newValue: string) => {
            setErrorMessage(null); // Reset error state
            setValue(newValue);

            // Only save if the value is valid AND has actually changed from the last saved value
            // This prevents unnecessary saves when components trigger onChange during initialization
            if (isValid(newValue) && newValue !== lastSavedValueRef.current) {
                lastSavedValueRef.current = newValue;
                handleSave(newValue).catch((err) => {
                    // If save failed, set the error message
                    const error = safelyGetGraphQLErrorMessage(err);
                    setErrorMessage(error);
                });
            }
        },
        [isValid, handleSave]
    );

    const onBlur = React.useCallback(() => {
        // If we didn't receive a valid value or the save failed
        // Reset value to saved value and reset error state
        if (!isValid(value) || errorMessage) {
            setValue(savedValue);
            setErrorMessage(null);
        }
    }, [isValid, value, errorMessage, savedValue]);

    return {
        value,
        errorMessage,
        onChange,
        onBlur,
    };
}
